/**
 * ============================================================================
 * VEDANG JYOTISHAM V6.5 — BTR CONTRACT & COVERAGE VALIDATION UTILITIES
 * ============================================================================
 * Specification: VJ-BTR-V65-SPEC-001 v3.0 (Phase 1 Foundation)
 * Validates Two-Axis Uncertainty, Candidate Tuples, Temporal Regions, Search
 * Coverage Audits, and 4-State Lifecycle Invariants.
 *
 * Strict Rule: Contains validation contracts and coverage accounting only.
 * No duplicate astronomical calculation logic or heuristic scoring.
 * ============================================================================
 */

import {
  TwoAxisBirthUncertainty,
  CandidateTuple,
  TemporalRegion,
  SearchCoverageAudit,
  SearchCoverageStatus,
  FourBtrStates,
  BtrLifecycleState,
  BtrLifeEventInput,
  LifeEventCategory,
  LifeEventTier,
  BTR_ERROR_INVALID_EVENT_PRECISION
} from '../types/btr';

export interface ContractValidationResult<T> {
  isValid: boolean;
  errors: string[];
  validatedData?: T;
}

export interface LifeEventsValidationResult extends ContractValidationResult<BtrLifeEventInput[]> {
  errorCode?: string;
}

// ============================================================================
// HELPER VALIDATORS
// ============================================================================

const DATE_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;

/**
 * Validates real Gregorian calendar date including leap years.
 */
export function isValidCalendarDate(dateStr: string): boolean {
  if (!DATE_REGEX.test(dateStr)) return false;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dateObj = new Date(0);
  dateObj.setUTCFullYear(y, m - 1, d);
  return (
    dateObj.getUTCFullYear() === y &&
    dateObj.getUTCMonth() === m - 1 &&
    dateObj.getUTCDate() === d
  );
}

/**
 * Validates clock time format HH:mm:ss.
 */
export function isValidClockTime(timeStr: string): boolean {
  return TIME_REGEX.test(timeStr);
}

/**
 * Validates IANA timezone string via native Intl API.
 */
export function isValidIanaTimezone(tz: string): boolean {
  if (!tz || typeof tz !== 'string') return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// ============================================================================
// 1. TWO-AXIS BIRTH UNCERTAINTY VALIDATION
// ============================================================================

export function validateTwoAxisUncertainty(
  input: unknown
): ContractValidationResult<TwoAxisBirthUncertainty> {
  const errors: string[] = [];
  if (!input || typeof input !== 'object') {
    return { isValid: false, errors: ['Input must be a non-null object'] };
  }

  const u = input as Partial<TwoAxisBirthUncertainty>;

  // Location validation
  if (!u.location || typeof u.location !== 'object') {
    errors.push('Missing or invalid location object');
  } else {
    const loc = u.location;
    if (typeof loc.latitude !== 'number' || loc.latitude < -90 || loc.latitude > 90) {
      errors.push('latitude must be a number between -90 and +90');
    }
    if (typeof loc.longitude !== 'number' || loc.longitude < -180 || loc.longitude > 180) {
      errors.push('longitude must be a number between -180 and +180');
    }
    if (!loc.placeName || typeof loc.placeName !== 'string' || loc.placeName.trim().length === 0) {
      errors.push('placeName must be a non-empty string');
    }
    if (!loc.timezoneId || !isValidIanaTimezone(loc.timezoneId)) {
      errors.push(`Invalid or unsupported IANA timezone identifier: "${loc.timezoneId}"`);
    }
  }

  // Axis B Date validation
  if (!u.axisBDate || typeof u.axisBDate !== 'object') {
    errors.push('Missing or invalid axisBDate object');
  } else {
    const d = u.axisBDate;
    if (!d.reportedLocalDate || !isValidCalendarDate(d.reportedLocalDate)) {
      errors.push(`Invalid reportedLocalDate: "${d.reportedLocalDate}"`);
    }
    if (!d.earliestLocalDate || !isValidCalendarDate(d.earliestLocalDate)) {
      errors.push(`Invalid earliestLocalDate: "${d.earliestLocalDate}"`);
    }
    if (!d.latestLocalDate || !isValidCalendarDate(d.latestLocalDate)) {
      errors.push(`Invalid latestLocalDate: "${d.latestLocalDate}"`);
    }

    if (isValidCalendarDate(d.earliestLocalDate) && isValidCalendarDate(d.latestLocalDate)) {
      const startMs = new Date(d.earliestLocalDate).getTime();
      const endMs = new Date(d.latestLocalDate).getTime();
      if (startMs > endMs) {
        errors.push('earliestLocalDate must be on or before latestLocalDate');
      }

      if (d.reportedLocalDate && isValidCalendarDate(d.reportedLocalDate)) {
        const repMs = new Date(d.reportedLocalDate).getTime();
        if (repMs < startMs || repMs > endMs) {
          errors.push('reportedLocalDate must fall within [earliestLocalDate, latestLocalDate]');
        }

        // Maximum envelope guard: ±2 years (max 731 days from reported date)
        const MS_PER_DAY = 24 * 60 * 60 * 1000;
        const diffStartDays = (repMs - startMs) / MS_PER_DAY;
        const diffEndDays = (endMs - repMs) / MS_PER_DAY;
        if (diffStartDays > 732 || diffEndDays > 732) {
          errors.push('Axis B date uncertainty envelope exceeds maximum supported limit of ±2 years');
        }
      }
    }

    if (!['EXACT', 'BOUNDED_RANGE', 'UNKNOWN_MULTI_YEAR'].includes(d.certainty as string)) {
      errors.push(`Invalid dateCertainty: "${d.certainty}"`);
    }
  }

  // Axis A Time validation
  if (!u.axisATime || typeof u.axisATime !== 'object') {
    errors.push('Missing or invalid axisATime object');
  } else {
    const t = u.axisATime;
    if (!t.reportedLocalTime || !isValidClockTime(t.reportedLocalTime)) {
      errors.push(`Invalid reportedLocalTime (HH:mm:ss): "${t.reportedLocalTime}"`);
    }
    if (!t.earliestLocalTime || !isValidClockTime(t.earliestLocalTime)) {
      errors.push(`Invalid earliestLocalTime (HH:mm:ss): "${t.earliestLocalTime}"`);
    }
    if (!t.latestLocalTime || !isValidClockTime(t.latestLocalTime)) {
      errors.push(`Invalid latestLocalTime (HH:mm:ss): "${t.latestLocalTime}"`);
    }

    if (!t.timeSource || typeof t.timeSource !== 'string') {
      errors.push('timeSource must be a non-empty string');
    }
    if (!['Exact', 'Minutes', 'QuarterHour', 'Hour', 'Approximate'].includes(t.precisionLevel as string)) {
      errors.push(`Invalid precisionLevel: "${t.precisionLevel}"`);
    }
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [], validatedData: input as TwoAxisBirthUncertainty };
}

// ============================================================================
// 2. CANDIDATE TUPLE VALIDATION
// ============================================================================

export function validateCandidateTuple(
  tuple: unknown
): ContractValidationResult<CandidateTuple> {
  const errors: string[] = [];
  if (!tuple || typeof tuple !== 'object') {
    return { isValid: false, errors: ['Candidate tuple must be a non-null object'] };
  }

  const c = tuple as Partial<CandidateTuple>;

  if (!c.localDate || !isValidCalendarDate(c.localDate)) {
    errors.push(`Invalid candidate localDate: "${c.localDate}"`);
  }
  if (!c.localClockTime || !isValidClockTime(c.localClockTime)) {
    errors.push(`Invalid candidate localClockTime: "${c.localClockTime}"`);
  }
  if (typeof c.latitude !== 'number' || c.latitude < -90 || c.latitude > 90) {
    errors.push('latitude must be between -90 and +90');
  }
  if (typeof c.longitude !== 'number' || c.longitude < -180 || c.longitude > 180) {
    errors.push('longitude must be between -180 and +180');
  }
  if (!c.timezoneId || !isValidIanaTimezone(c.timezoneId)) {
    errors.push(`Invalid timezoneId: "${c.timezoneId}"`);
  }
  if (typeof c.utcOffsetSeconds !== 'number' || c.utcOffsetSeconds < -43200 || c.utcOffsetSeconds > 50400) {
    errors.push('utcOffsetSeconds must be between -43200 (UTC-12) and +50400 (UTC+14)');
  }
  if (!c.utcInstant || isNaN(Date.parse(c.utcInstant))) {
    errors.push(`Invalid utcInstant ISO string: "${c.utcInstant}"`);
  }
  if (!c.sourceProvenance || typeof c.sourceProvenance !== 'string') {
    errors.push('sourceProvenance must be a non-empty string');
  }
  if (!c.calculationVersion || typeof c.calculationVersion !== 'string') {
    errors.push('calculationVersion must be a non-empty string');
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [], validatedData: tuple as CandidateTuple };
}

// ============================================================================
// 3. TEMPORAL REGION VALIDATION
// ============================================================================

export function validateTemporalRegion(
  region: unknown
): ContractValidationResult<TemporalRegion> {
  const errors: string[] = [];
  if (!region || typeof region !== 'object') {
    return { isValid: false, errors: ['Region must be a non-null object'] };
  }

  const r = region as Partial<TemporalRegion>;
  if (!r.startUtc || isNaN(Date.parse(r.startUtc))) {
    errors.push(`Invalid startUtc: "${r.startUtc}"`);
  }
  if (!r.endUtc || isNaN(Date.parse(r.endUtc))) {
    errors.push(`Invalid endUtc: "${r.endUtc}"`);
  }
  if (r.startUtc && r.endUtc && Date.parse(r.startUtc) > Date.parse(r.endUtc)) {
    errors.push('startUtc must be on or before endUtc');
  }
  if (!['EVALUATED', 'PRUNED', 'UNEVALUATED'].includes(r.regionType as string)) {
    errors.push(`Invalid regionType: "${r.regionType}"`);
  }
  if (!r.reason || typeof r.reason !== 'string' || r.reason.trim().length === 0) {
    errors.push('reason must be a non-empty string');
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [], validatedData: region as TemporalRegion };
}

// ============================================================================
// 4. SEARCH COVERAGE STATUS COMPUTATION & AUDIT VALIDATION
// ============================================================================

/**
 * Computes search coverage status adhering strictly to completeness invariants:
 * - If any unevaluated region exists OR evaluation budget was exceeded,
 *   coverageStatus CANNOT claim 'EXHAUSTIVE' or 'PRUNED_COMPLETE'.
 *   It must return 'TRUNCATED_BY_BUDGET'.
 */
export function computeSearchCoverageStatus(params: {
  evaluatedRegions: TemporalRegion[];
  prunedRegions: TemporalRegion[];
  unevaluatedRegions: TemporalRegion[];
  budgetExceeded: boolean;
  totalTheoreticalSeconds: number;
}): SearchCoverageStatus {
  const { evaluatedRegions, prunedRegions, unevaluatedRegions, budgetExceeded, totalTheoreticalSeconds } = params;

  if (totalTheoreticalSeconds <= 0 || evaluatedRegions.length === 0) {
    return 'INSUFFICIENT_DATA';
  }

  if (budgetExceeded || unevaluatedRegions.length > 0) {
    return 'TRUNCATED_BY_BUDGET';
  }

  if (prunedRegions.length === 0) {
    return 'EXHAUSTIVE';
  }

  return 'PRUNED_COMPLETE';
}

/**
 * Validates SearchCoverageAudit integrity.
 */
export function validateSearchCoverageAudit(
  audit: unknown
): ContractValidationResult<SearchCoverageAudit> {
  const errors: string[] = [];
  if (!audit || typeof audit !== 'object') {
    return { isValid: false, errors: ['SearchCoverageAudit must be a non-null object'] };
  }

  const a = audit as Partial<SearchCoverageAudit>;

  if (!a.budgetLimits || typeof a.budgetLimits !== 'object') {
    errors.push('Missing budgetLimits object');
  } else {
    if (typeof a.budgetLimits.maxEvaluationsAllowed !== 'number' || a.budgetLimits.maxEvaluationsAllowed <= 0) {
      errors.push('maxEvaluationsAllowed must be a positive integer');
    }
    if (typeof a.budgetLimits.actualEvaluationsExecuted !== 'number' || a.budgetLimits.actualEvaluationsExecuted < 0) {
      errors.push('actualEvaluationsExecuted must be a non-negative integer');
    }
  }

  if (!Array.isArray(a.evaluatedRegions)) {
    errors.push('evaluatedRegions must be an array');
  }
  if (!Array.isArray(a.prunedRegions)) {
    errors.push('prunedRegions must be an array');
  }
  if (!Array.isArray(a.unevaluatedRegions)) {
    errors.push('unevaluatedRegions must be an array');
  }

  // Completeness Invariant Enforcement
  const unevaluatedCount = Array.isArray(a.unevaluatedRegions) ? a.unevaluatedRegions.length : 0;
  const budgetExceeded = a.budgetLimits?.budgetExceeded ?? false;

  if (unevaluatedCount > 0 || budgetExceeded) {
    if (a.coverageStatus === 'EXHAUSTIVE' || a.coverageStatus === 'PRUNED_COMPLETE') {
      errors.push(
        `Coverage status invariant violation: coverageStatus cannot be "${a.coverageStatus}" when unevaluated regions exist (${unevaluatedCount}) or budget is exceeded (${budgetExceeded}).`
      );
    }
  }

  if (typeof a.completenessRatio !== 'number' || a.completenessRatio < 0 || a.completenessRatio > 1.0) {
    errors.push('completenessRatio must be a float between 0.0 and 1.0');
  }

  // 3. Region Integrity, Pairwise Disjointness & Exact Duration Conservation (FINDING-P3-07)
  const allRegions: TemporalRegion[] = [
    ...(Array.isArray(a.evaluatedRegions) ? a.evaluatedRegions : []),
    ...(Array.isArray(a.prunedRegions) ? a.prunedRegions : []),
    ...(Array.isArray(a.unevaluatedRegions) ? a.unevaluatedRegions : [])
  ];

  interface NormalizedRegion {
    startSec: number;
    endSec: number;
    startUtc: string;
    endUtc: string;
    regionType: string;
  }

  const normalized: NormalizedRegion[] = [];
  for (const reg of allRegions) {
    if (!reg || typeof reg !== 'object' || typeof reg.startUtc !== 'string' || typeof reg.endUtc !== 'string') {
      errors.push('Region must be an object with valid startUtc and endUtc strings');
      continue;
    }
    const s = Math.floor(new Date(reg.startUtc).getTime() / 1000);
    const e = Math.floor(new Date(reg.endUtc).getTime() / 1000);

    if (isNaN(s) || isNaN(e)) {
      errors.push(`Invalid timestamp in region: [${reg.startUtc}, ${reg.endUtc})`);
      continue;
    }

    if (s >= e) {
      errors.push(`Region interval integrity violation: startUtc must be strictly before endUtc for [${reg.startUtc}, ${reg.endUtc})`);
      continue;
    }

    normalized.push({
      startSec: s,
      endSec: e,
      startUtc: reg.startUtc,
      endUtc: reg.endUtc,
      regionType: reg.regionType || 'UNKNOWN'
    });
  }

  // Sort normalized regions by startSec, then endSec
  normalized.sort((x, y) => x.startSec - y.startSec || x.endSec - y.endSec);

  // Check pairwise disjointness (no overlaps) and contiguity (no gaps)
  for (let i = 0; i < normalized.length - 1; i++) {
    const curr = normalized[i];
    const next = normalized[i + 1];

    if (curr.endSec > next.startSec) {
      errors.push(
        `Coverage disjointness violation: regions overlap between [${curr.startUtc}, ${curr.endUtc}) and [${next.startUtc}, ${next.endUtc})`
      );
    } else if (curr.endSec < next.startSec) {
      errors.push(
        `Coverage gap violation: unallocated interval between [${curr.endUtc}, ${next.startUtc})`
      );
    }
  }

  // Exact Duration Conservation & Envelope Boundary Anchoring (FINDING-P3-07, FINDING-P3-09)
  const totalTheoretical = a.requestedEnvelope?.totalTheoreticalSeconds ?? 0;
  if (totalTheoretical > 0 && a.coverageStatus !== 'INSUFFICIENT_DATA') {
    if (normalized.length === 0) {
      errors.push('Coverage partition cannot be empty for a non-empty requested envelope');
    } else {
      const totalCoveredSeconds = normalized.reduce((acc, r) => acc + (r.endSec - r.startSec), 0);
      if (totalCoveredSeconds !== totalTheoretical) {
        errors.push(
          `Duration conservation violation: total covered seconds (${totalCoveredSeconds}) does not equal requested envelope (${totalTheoretical})`
        );
      }

      // Bi-Directional Envelope Boundary Anchoring (FINDING-P3-10)
      const rawDateStart = a.requestedEnvelope?.axisBDateRange?.start;
      const rawDateEnd = a.requestedEnvelope?.axisBDateRange?.end;
      const rawTimeStart = a.requestedEnvelope?.axisATimeRange?.start;
      const rawTimeEnd = a.requestedEnvelope?.axisATimeRange?.end;

      const parseBoundaryInstant = (
        rawDate: string | undefined,
        rawTime: string | undefined,
        boundaryName: 'start' | 'end'
      ): number | null => {
        if (!rawDate || !rawTime || typeof rawDate !== 'string' || typeof rawTime !== 'string') {
          errors.push(
            `Coverage envelope missing boundary fields: ${boundaryName} date and time strings are required`
          );
          return null;
        }

        const trimmedDate = rawDate.trim();
        const trimmedTime = rawTime.trim();
        const cleanDate = trimmedDate.includes('T') ? trimmedDate.split('T')[0] : trimmedDate;
        const cleanTime = trimmedTime.replace(/Z$/, '').split('.')[0];

        if (!isValidCalendarDate(cleanDate)) {
          errors.push(
            `Coverage envelope boundary formatting violation: invalid ${boundaryName} calendar date "${rawDate}"`
          );
          return null;
        }

        if (!isValidClockTime(cleanTime)) {
          errors.push(
            `Coverage envelope boundary formatting violation: invalid ${boundaryName} clock time "${rawTime}"`
          );
          return null;
        }

        const iso = `${cleanDate}T${cleanTime}Z`;
        const ms = Date.parse(iso);
        if (isNaN(ms)) {
          errors.push(
            `Coverage envelope boundary parsing violation: unable to parse ${boundaryName} instant "${iso}"`
          );
          return null;
        }

        return Math.floor(ms / 1000);
      };

      const envStartSec = parseBoundaryInstant(rawDateStart, rawTimeStart, 'start');
      const envEndSec = parseBoundaryInstant(rawDateEnd, rawTimeEnd, 'end');

      if (envStartSec !== null && envEndSec !== null) {
        if (envEndSec <= envStartSec) {
          errors.push(
            `Coverage envelope inverted or zero-length violation: start boundary (${new Date(envStartSec * 1000).toISOString()}) must be strictly before end boundary (${new Date(envEndSec * 1000).toISOString()})`
          );
        } else {
          const theoreticalSpan = envEndSec - envStartSec;
          if (theoreticalSpan !== totalTheoretical) {
            errors.push(
              `Coverage envelope duration mismatch: continuous interval [${new Date(envStartSec * 1000).toISOString()}, ${new Date(envEndSec * 1000).toISOString()}) has span (${theoreticalSpan}s), but totalTheoreticalSeconds is (${totalTheoretical}s)`
            );
          }

          const partitionStartSec = normalized[0].startSec;
          const partitionEndSec = normalized[normalized.length - 1].endSec;

          if (partitionStartSec !== envStartSec) {
            errors.push(
              `Coverage partition start violation: partition begins at ${normalized[0].startUtc} (${partitionStartSec}), expected envelope start ${new Date(envStartSec * 1000).toISOString()} (${envStartSec})`
            );
          }

          if (partitionEndSec !== envEndSec) {
            errors.push(
              `Coverage partition end violation: partition ends at ${normalized[normalized.length - 1].endUtc} (${partitionEndSec}), expected envelope end ${new Date(envEndSec * 1000).toISOString()} (${envEndSec})`
            );
          }

          for (const r of normalized) {
            if (r.startSec < envStartSec || r.endSec > envEndSec) {
              errors.push(
                `Coverage region out-of-bounds violation: region [${r.startUtc}, ${r.endUtc}) lies outside requested envelope [${new Date(envStartSec * 1000).toISOString()}, ${new Date(envEndSec * 1000).toISOString()})`
              );
            }
          }
        }
      }
    }
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [], validatedData: audit as SearchCoverageAudit };
}

// ============================================================================
// 5. FOUR BTR STATES VALIDATION & LIFECYCLE INVARIANTS
// ============================================================================

export function validateFourBtrStates(
  states: unknown
): ContractValidationResult<FourBtrStates> {
  const errors: string[] = [];
  if (!states || typeof states !== 'object') {
    return { isValid: false, errors: ['FourBtrStates must be a non-null object'] };
  }

  const s = states as Partial<FourBtrStates>;

  if (!s.recordedTime || !isValidClockTime(s.recordedTime)) {
    errors.push(`Invalid recordedTime: "${s.recordedTime}"`);
  }

  const validLifecycles: BtrLifecycleState[] = ['RECORDED', 'RECTIFIED_INTERVAL', 'WORKING', 'CONFIRMED', 'STALE'];
  if (!validLifecycles.includes(s.lifecycleState as BtrLifecycleState)) {
    errors.push(`Invalid lifecycleState: "${s.lifecycleState}"`);
  }

  // Stale Invariant: confirmedTime must be null when lifecycleState is STALE
  if (s.lifecycleState === 'STALE') {
    if (s.confirmedTime !== null) {
      errors.push('Lifecycle invariant violation: confirmedTime must be null when state is STALE');
    }
    if (s.isProvisional !== true) {
      errors.push('Lifecycle invariant violation: isProvisional must be true when state is STALE');
    }
  }

  // Provisional Invariant: workingTime unconfirmed requires isProvisional = true
  if (s.lifecycleState === 'WORKING' && s.confirmedTime === null && s.isProvisional !== true) {
    errors.push('Lifecycle invariant violation: isProvisional must be true when workingTime is unconfirmed');
  }

  if (!Array.isArray(s.provenanceLedger)) {
    errors.push('provenanceLedger must be an array');
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [], validatedData: states as FourBtrStates };
}

// ============================================================================
// 6. LIFE EVENTS INPUT & PRECISION CONTRACT VALIDATION
// ============================================================================

export const CANONICAL_LIFE_EVENT_CATEGORIES: readonly LifeEventCategory[] = [
  'MARRIAGE',
  'CHILDREN',
  'CAREER',
  'BUSINESS',
  'RELOCATION',
  'PROPERTY',
  'HEALTH',
  'FAMILY',
  'EDUCATION',
  'FINANCE',
  'LEGAL',
  'SPIRITUAL',
  'OTHER'
] as const;

export const VALID_LIFE_EVENT_TIERS: readonly LifeEventTier[] = [
  'TIER_A',
  'TIER_B',
  'TIER_C'
] as const;

const PARTIAL_YEAR_REGEX = /^\d{4}$/;
const PARTIAL_MONTH_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Validates BTR life events input array.
 * Enforces:
 * 1. Array contract: input must be an array (can be empty).
 * 2. Structure contract: each event must be a non-null object with valid id, category, tier, date.
 * 3. Date precision contract:
 *    - YYYY or YYYY-MM with TIER_A is rejected with errorCode BTR_INVALID_EVENT_PRECISION.
 *    - Caller-supplied isExact: true cannot override date precision derived from date string.
 *    - Exact YYYY-MM-DD must be a valid Gregorian calendar date (via isValidCalendarDate).
 *    - Malformed dates (e.g. 2015-13) and impossible calendar dates (e.g. 2023-02-29) are rejected.
 *    - Optional time must satisfy isValidClockTime if provided.
 */
export function validateBtrLifeEvents(
  input: unknown
): LifeEventsValidationResult {
  if (!Array.isArray(input)) {
    return {
      isValid: false,
      errors: ['Life events input must be an array']
    };
  }

  const errors: string[] = [];
  let errorCode: string | undefined;

  for (let i = 0; i < input.length; i++) {
    const raw = input[i];
    const prefix = `Life event at index ${i}`;

    if (!raw || typeof raw !== 'object') {
      errors.push(`${prefix}: must be a non-null object`);
      continue;
    }

    const event = raw as Partial<BtrLifeEventInput>;
    const eventIdentifier = event.id ? `"${event.id}"` : `at index ${i}`;

    // 1. ID validation
    if (!event.id || typeof event.id !== 'string' || event.id.trim() === '') {
      errors.push(`${prefix}: missing or empty "id"`);
    }

    // 2. Category validation
    if (!event.category || !CANONICAL_LIFE_EVENT_CATEGORIES.includes(event.category as LifeEventCategory)) {
      errors.push(
        `Life event ${eventIdentifier}: invalid category "${event.category}". Must be one of: ${CANONICAL_LIFE_EVENT_CATEGORIES.join(', ')}`
      );
    }

    // 3. Tier validation
    if (!event.tier || !VALID_LIFE_EVENT_TIERS.includes(event.tier as LifeEventTier)) {
      errors.push(
        `Life event ${eventIdentifier}: invalid tier "${event.tier}". Must be one of: ${VALID_LIFE_EVENT_TIERS.join(', ')}`
      );
    }

    // 4. Date validation & Precision contract
    if (!event.date || typeof event.date !== 'string') {
      errors.push(`Life event ${eventIdentifier}: missing or invalid "date" string`);
    } else {
      const isYear = PARTIAL_YEAR_REGEX.test(event.date);
      const isYearMonth = PARTIAL_MONTH_REGEX.test(event.date);
      const isFullFormat = DATE_REGEX.test(event.date);

      if (isYear || isYearMonth) {
        // Date is a partial date (YYYY or YYYY-MM)
        if (event.tier === 'TIER_A') {
          // Tier A requires exact date. Partial date is strictly rejected.
          errorCode = BTR_ERROR_INVALID_EVENT_PRECISION;
          errors.push(
            `Life event ${eventIdentifier} has tier TIER_A but specifies partial date "${event.date}". Tier A events require an exact Gregorian calendar date (YYYY-MM-DD).`
          );
        }
        // If TIER_B or TIER_C, partial date is accepted as valid input.
      } else if (isFullFormat) {
        // Must validate calendar reality (leap years, 28/29/30/31 days)
        if (!isValidCalendarDate(event.date)) {
          errors.push(
            `Life event ${eventIdentifier} has impossible calendar date "${event.date}". Date does not exist on Gregorian calendar.`
          );
        }
      } else {
        // Malformed date string
        errors.push(
          `Life event ${eventIdentifier} has invalid date format "${event.date}". Date must be YYYY, YYYY-MM, or YYYY-MM-DD.`
        );
      }
    }

    // 5. Time validation (optional)
    if (event.time !== undefined && event.time !== null && event.time !== '') {
      if (typeof event.time !== 'string' || !isValidClockTime(event.time)) {
        errors.push(
          `Life event ${eventIdentifier}: invalid time "${event.time}". Must be HH:mm:ss.`
        );
      }
    }
  }

  if (errors.length > 0) {
    return {
      isValid: false,
      errors,
      ...(errorCode ? { errorCode } : {})
    };
  }

  return {
    isValid: true,
    errors: [],
    validatedData: input as BtrLifeEventInput[]
  };
}

